// Shift-JIS encoding utilities for CSV export

const SHIFT_JIS_LEAD_BYTES: number[] = [
  ...Array.from({ length: 0x9F - 0x81 + 1 }, (_, i) => 0x81 + i),
  ...Array.from({ length: 0xEF - 0xE0 + 1 }, (_, i) => 0xE0 + i)
];

const SHIFT_JIS_TRAIL_BYTES: number[] = [
  ...Array.from({ length: 0x7E - 0x40 + 1 }, (_, i) => 0x40 + i),
  ...Array.from({ length: 0xFC - 0x80 + 1 }, (_, i) => 0x80 + i)
];

const SHIFT_JIS_DECODER: TextDecoder | null = (() => {
  if (typeof TextDecoder === 'undefined') {
    return null;
  }
  try {
    return new TextDecoder('shift_jis');
  } catch (error) {
    console.warn('Shift_JIS decoder is not available in this environment. Falling back to UTF-8 with BOM.', error);
    return null;
  }
})();

const SHIFT_JIS_PROBE_BUFFER = new Uint8Array(2);
const SHIFT_JIS_CACHE = new Map<string, number[]>();

/**
 * Shift-JIS 非対応文字を標準字体に正規化するマップ（コードポイントベース）
 * key: 変換元のコードポイント, value: 変換先の文字
 */
const CJK_CODEPOINT_MAP = new Map<number, string>([
  // 高/髙 の異体字
  [0x9AD9, '\u9AD8'],   // 髙 → 高
  [0xFA09, '\u9AD8'],   // 高 (CJK互換) → 高

  // 橋 の異体字
  [0x23632, '\u6A4B'],  // 𣘺 → 橋
  [0x2F91F, '\u6A4B'],  // 橋 (CJK互換補助) → 橋
  [0x27C11, '\u6A4B'],  // 橋 variant → 橋

  // 崎/﨑
  [0xFA11, '\u5D0E'],   // 﨑 → 崎

  // 吉/𠮷
  [0x20BB7, '\u5409'],  // 𠮷 → 吉
  [0x2000B, '\u5409'],  // 𠀋 → 吉

  // 濱/浜
  [0x6FF1, '\u6D5C'],   // 濱 → 浜

  // 澤/沢
  [0x6FA4, '\u6CA2'],   // 澤 → 沢

  // 邉/邊/辺
  [0x9089, '\u8FBA'],   // 邉 → 辺
  [0x908A, '\u8FBA'],   // 邊 → 辺

  // 廣/広
  [0x5EE3, '\u5E83'],   // 廣 → 広

  // 歸/帰
  [0x6B78, '\u5E30'],   // 歸 → 帰

  // 齊/斉, 齋/斎
  [0x9F4A, '\u6589'],   // 齊 → 斉
  [0x9F4B, '\u658E'],   // 齋 → 斎

  // 鷗
  [0x9DD7, '\u9D5C'],   // 鷗 → 鴜

  // 瀨/瀬
  [0x7028, '\u702C'],   // 瀨 → 瀬

  // 條/条
  [0x689D, '\u6761'],   // 條 → 条

  // 國/国
  [0x570B, '\u56FD'],   // 國 → 国

  // 島 の異体字
  [0xFA19, '\u5CF6'],   // 嶋 (CJK互換) → 島

  // 塚 の異体字
  [0xFA10, '\u585A'],   // 塚 (CJK互換) → 塚

  // 德/徳
  [0x5FB7, '\u5FB3'],   // 德 → 徳
]);

/**
 * Shift-JIS非対応文字を標準字体に正規化
 * 1. NFKC正規化（CJK互換漢字の多くを標準形に変換）
 * 2. コードポイントベースのカスタムマップで残りを変換
 */
const normalizeCjk = (input: string): string => {
  // NFKC正規化: CJK Compatibility Ideographs (U+F900-U+FA6D) の多くを標準形に変換
  const nfkc = input.normalize('NFKC');

  let result = '';
  for (const char of nfkc) {
    const cp = char.codePointAt(0);
    if (cp !== undefined && CJK_CODEPOINT_MAP.has(cp)) {
      result += CJK_CODEPOINT_MAP.get(cp);
    } else {
      result += char;
    }
  }
  return result;
};

export const encodeShiftJIS = (input: string): Uint8Array => {
  if (!SHIFT_JIS_DECODER) {
    const encoder = new TextEncoder();
    return encoder.encode(`\uFEFF${input}`);
  }

  const normalized = normalizeCjk(input);
  const bytes: number[] = [];
  const unmapped: string[] = [];

  for (const char of normalized) {
    const codePoint = char.codePointAt(0);
    if (codePoint === undefined) {
      continue;
    }

    if (codePoint <= 0x7F) {
      bytes.push(codePoint);
      continue;
    }

    if (codePoint >= 0xFF61 && codePoint <= 0xFF9F) {
      bytes.push(codePoint - 0xFF61 + 0xA1);
      continue;
    }

    let cached = SHIFT_JIS_CACHE.get(char);
    if (cached) {
      bytes.push(...cached);
      continue;
    }

    let found = false;
    for (const lead of SHIFT_JIS_LEAD_BYTES) {
      for (const trail of SHIFT_JIS_TRAIL_BYTES) {
        SHIFT_JIS_PROBE_BUFFER[0] = lead;
        SHIFT_JIS_PROBE_BUFFER[1] = trail;
        const decoded = SHIFT_JIS_DECODER.decode(SHIFT_JIS_PROBE_BUFFER);
        if (decoded === char) {
          const pair = [lead, trail];
          SHIFT_JIS_CACHE.set(char, pair);
          bytes.push(...pair);
          found = true;
          break;
        }
      }
      if (found) break;
    }

    if (!found) {
      unmapped.push(`${char}(U+${codePoint.toString(16).toUpperCase()})`);
      bytes.push(0x3F); // '?'
    }
  }

  if (unmapped.length > 0) {
    console.warn('[CSV] Shift-JIS変換不可文字:', unmapped.join(', '));
  }

  return new Uint8Array(bytes);
};

export const escapeCsvCell = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export const toCsvLine = (values: (string | number | null | undefined)[]): string => {
  return values.map(escapeCsvCell).join(',');
};

export const downloadCsvBlob = (csvContent: string, filename: string): void => {
  const encoder = new TextEncoder();
  const csvBuffer = encoder.encode(`\uFEFF${csvContent}`);
  const blob = new Blob([csvBuffer], { type: 'text/csv;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(link.href), 0);
};
